import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../api";
import type { User } from "../App";
import { useTurnstile } from "../use-turnstile";

export default function ResetPassword({ onLogin }: { onLogin: (u: User) => void }) {
  const [params] = useSearchParams();
  const token = params.get("token")?.trim() ?? "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [turnstileSiteKey, setTurnstileSiteKey] = useState<string | null>(null);
  const hasToken = Boolean(token);
  const { containerRef, getToken, reset, loadingConfig, required: turnstileRequired } = useTurnstile(
    turnstileSiteKey,
    hasToken,
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch("/api/auth/config");
        const j = (await r.json()) as { turnstileSiteKey?: string };
        if (!cancelled) setTurnstileSiteKey(j.turnstileSiteKey?.trim() ? j.turnstileSiteKey : "");
      } catch {
        if (!cancelled) setTurnstileSiteKey("");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== password2) {
      setError("Passwords do not match.");
      return;
    }
    if (!token) {
      setError("Missing reset token. Open the link from your email.");
      return;
    }
    if (turnstileRequired && !getToken()) {
      setError("Complete the security check before saving your new password.");
      return;
    }
    setBusy(true);
    const r = await api<{ user: User }>("/api/auth/reset-password", {
      method: "POST",
      body: JSON.stringify({
        token,
        password,
        ...(turnstileRequired ? { turnstileToken: getToken() } : {}),
      }),
    });
    setBusy(false);
    if (!r.ok) {
      setError(r.error || "Reset failed");
      reset();
      return;
    }
    if (r.data?.user) {
      onLogin(r.data.user);
      navigate("/record", { replace: true });
    }
  }

  return (
    <div className="layout">
      <main className="layout-main layout-main--narrow">
        <h1>New password</h1>
        {!token ? (
          <p className="error">
            This page needs a <code>token</code> from your reset email.{" "}
            <Link to="/forgot-password">Request a new link</Link>.
          </p>
        ) : (
          <>
            <p className="muted">Choose a new password for your account.</p>
            <form className="card" onSubmit={submit}>
              <div className="field">
                <label htmlFor="rp-pw">New password</label>
                <input
                  id="rp-pw"
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                />
              </div>
              <div className="field">
                <label htmlFor="rp-pw2">Confirm password</label>
                <input
                  id="rp-pw2"
                  type="password"
                  autoComplete="new-password"
                  value={password2}
                  onChange={(e) => setPassword2(e.target.value)}
                  required
                  minLength={8}
                />
              </div>
              {turnstileRequired ? (
                <div className="field turnstile-field">
                  <span className="turnstile-label">Verification</span>
                  <div ref={containerRef} className="turnstile-container" />
                </div>
              ) : null}
              {error ? <p className="error">{error}</p> : null}
              <button
                type="submit"
                className="btn btn-primary"
                disabled={busy || loadingConfig}
                style={{ width: "100%" }}
              >
                {busy ? "Saving…" : "Save password"}
              </button>
            </form>
          </>
        )}
        <p className="muted" style={{ marginTop: "1rem" }}>
          <Link to="/login">Sign in</Link>
        </p>
      </main>
    </div>
  );
}
